/**
 * @owner @Appboy/operator
 */

/**
 * Canonical reasons for keeping an Operator-readable control human-only.
 *
 * Keep these values developer-facing and untranslated. Add a reason only when the control should
 * remain in `data-operator-description` but must not register its own tool. Destructive actions
 * should normally require approval, sensitive values should use the `sensitive` APIs, and content
 * that should not appear in the page description should use the scraper-blocking mechanism instead.
 *
 * Keeping the reasons as named properties makes usage countable by reason across the dashboard.
 * From `dashboard/`, run:
 * `rg --no-filename -o 'operatorDisabledReasons\.[A-Za-z]+' app/javascript/src --glob '!*.stories.*' --glob '!*.test.*' --glob '!*.cy.*' | sort | uniq -c`
 */
export const operatorDisabledReasons = {
  agenticActionToolOwnsAction: "useAgenticActionTool owns the Operator action",
  agentModelSelectionToolOwnsAction: "Agent model selection tool owns the Operator action",
  brazeCheckboxGroupOwnsAction: "BrazeCheckboxGroup owns the Operator action",
  brazeDataTableLayoutOwnsAction: "BrazeDataTableLayout owns the Operator action",
  brazeMenuOwnsAction: "BrazeMenu owns the Operator action",
  brazeModalOwnsAction: "BrazeModal owns the Operator action",
  brazeRadioButtonGroupOwnsAction: "BrazeRadioButtonGroup owns the Operator action",
  brazeSelectOwnsAction: "BrazeSelect owns the Operator action",
  tabsWithMenuOwnsAction: "TabsWithMenu owns the Operator action",
  calculationQueueToolOwnsAction: "Calculation queue tool owns cancellation by request id",
  formFieldOwnsAction: "Form field owns the Operator action",
  navigationLinkOwnsAction: "Navigation link owns the Operator action",
  primaryInstanceOwnsAction: "Another mounted instance of this control owns the Operator action",
  directFileDownload: "Operator cannot initiate direct file downloads",
  humanFeedbackRequired: "Feedback must be provided directly by the human user",
  operatorPacketFeatureDisabled: "Operator packet feature is disabled",
  externalAuthenticationRequiringHumanCompletion: "External authentication and consent require human completion",
  fileUploadRequiresHumanSelection: "File upload requires a human to select a local file",
  requiresClosingDragAndDropEditor: "Close the drag-and-drop editor before selecting this variant",
  explicitOperatorOnboardingRequired: "Control requires explicit Operator onboarding",
  financialOrContractualActionRequiringHumanCompletion:
    "Operator cannot perform financial or contractual actions requiring human completion",
  irreversibleWorkspaceChangeRequiringHumanConfirmation:
    "Operator cannot perform irreversible workspace changes requiring human confirmation",
  impersonationOrSupport: "Operator cannot perform impersonation or support-only actions",
  internalUseOnly: "Operator cannot interact with internal-use-only controls",
  preventsOperatorFromContinuing: "Action prevents Operator from continuing",
  readOnlyStatusIndicator: "Operator cannot interact with read-only status indicators",
  controlledByParent: "Controlled by parent",
  stepToolOwnsAutosavingField: "A step-scoped Operator tool owns this autosaving control",
  personalWarningPreference: "Warning dismissal preferences should be changed directly by the user",
} as const;

export type OperatorDisabledReason = (typeof operatorDisabledReasons)[keyof typeof operatorDisabledReasons];
